import React, { useEffect, useState } from "react";
import { Quote, Send, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Surfaces a creator's best (pinned) fan cheers on their game pages + lets players cheer them.
export default function CheerWall({ creatorId, creatorName }) {
  const { user } = useAuth();
  const [cheers, setCheers] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [cheered, setCheered] = useState(false);

  useEffect(() => {
    if (!creatorId) return;
    let alive = true;
    api.get(`/social/creators/${creatorId}/pinned-cheers`, { params: { limit: 6 } })
      .then((r) => { if (alive) setCheers(r.data?.cheers || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [creatorId]);

  const canCheer = user && creatorId && user.id !== creatorId;
  if (!cheers.length && !canCheer) return null;

  const send = async () => {
    const m = msg.trim();
    if (!m) return;
    setBusy(true);
    try {
      await api.post(`/social/creators/${creatorId}/cheer`, { message: m });
      setMsg(""); setCheered(true);
      toast.success(`Cheer sent to ${creatorName || "the creator"}! 📣`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.05] p-5" data-testid="cheer-wall">
      <h3 className="font-display font-bold mb-3 flex items-center gap-2">
        <Quote className="w-5 h-5 text-amber-500" /> Fans love {creatorName || "this creator"}
      </h3>

      {cheers.length > 0 && (
        <div className="space-y-2.5 mb-3">
          {cheers.map((c) => (
            <div key={c.id} data-testid={`cheer-wall-item-${c.id}`}
              className="flex items-start gap-2.5 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-2.5">
              <span className="text-xl shrink-0 leading-none mt-0.5">{c.fan_avatar}</span>
              <div className="min-w-0">
                <p className="text-sm leading-snug break-words">“{c.message}”</p>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-1">
                  — {c.fan_name}{c.creator_hearted && <span title="Loved by the creator">❤️</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {canCheer && (
        cheered ? (
          <p className="text-sm text-emerald-500 font-semibold" data-testid="cheer-wall-sent">Thanks for the love! 💛 Your cheer is on their profile.</p>
        ) : (
          <div className="flex gap-2" data-testid="cheer-wall-form">
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={200}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }}
              placeholder={`Cheer ${creatorName || "this creator"}…`}
              data-testid="cheer-wall-input" className="rounded-full bg-[hsl(var(--card))]" />
            <Button onClick={send} disabled={busy || !msg.trim()} data-testid="cheer-wall-submit"
              className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )
      )}
    </div>
  );
}
